"use client";

/**
 * Public `TokenSelector` entry point. Thin dispatcher over two internal modes:
 *   - `TokenSelectorRelayMode` (default)  — Relay currencies + SimDune fallback, EVM token picking
 *     with wallet balances, chain filter, AnySpend CTA, address paste.
 *   - `GenericPickerMode` (opt-in via `dataSource` prop) — domain-agnostic picker backed by
 *     caller-supplied data hooks + render slots (e.g. CoinGecko coin-id picking).
 *
 * The dispatcher itself holds zero hooks, so the branch chosen per-render is just
 * component-tree switching — each mode owns its own hook order unconditionally, satisfying
 * React's Rules of Hooks.
 */
import { c as _c } from "react/compiler-runtime";
import { memo } from "react";
import { GenericPickerMode, type PickerDataSource } from "./TokenSelector.generic";
import TokenSelectorRelayMode from "./TokenSelector.relay";

// Re-export so existing consumers (e.g. `useCoinGeckoSearch.tsx`) keep importing
// `PickerDataSource` from TokenSelector without caring about the internal file split.
export type { PickerDataSource };
export interface TokenSelectorProps {
  label: string;
  description?: string;
  headerRight?: React.ReactNode;
  value: string | string[];
  onChange: (value: string | string[]) => void;
  onChainIdChange?: (chainId: number) => void;
  placeholder?: string;
  required?: boolean;
  chainId?: number;
  chainIds?: number[]; // Optional array of chain IDs to filter by (takes precedence over chainId)
  workflowWalletAddress?: string; // Turnkey wallet address for transfer
  showAnySpendFundingCta?: boolean; // Show AnySpend funding CTA for selected token
  multiple?: boolean; // Enable multi-select mode
  /** When true, Popover uses modal mode (required inside dialogs to receive pointer events) */
  popoverModal?: boolean;
  /** Allowed chain IDs from schema enum — restricts the built-in chain filter */
  allowedChainIds?: number[];
  /** When true, show built-in chain filter dropdown (used when chain-id field is hidden) */
  showChainFilter?: boolean;
  /** When true, hide native tokens (the field only accepts contract-address tokens, e.g. ERC-20) */
  excludeNative?: boolean;
  /** When true, restrict token/chain selection to EVM chains */
  evmOnly?: boolean;

  // --- Generic data source override (advanced) ---
  // When `dataSource` is supplied, TokenSelector skips its default Relay path and renders
  // a generic search picker backed by the provided hooks + render slots. Token-specific
  // props above (chainId, workflowWalletAddress, showChainFilter, etc.) are ignored.
  /** Data source hooks (search + optional lookup). Presence of this prop enables generic mode. */
  dataSource?: PickerDataSource<unknown>;
  /** Stable identity for items — how a cache key / selected value is derived. */
  getKey?: (item: unknown) => string;
  /** Row renderer inside the dropdown. Required when `dataSource` is provided. */
  renderRow?: (item: unknown, ctx: {
    selected: boolean;
  }) => React.ReactNode;
  /** Trigger-button renderer when a single value is selected. Required when `dataSource` is provided. */
  renderTriggerValue?: (item: unknown | null, raw: string) => React.ReactNode;
  /** Multi-select chip renderer. Required for multi mode when `dataSource` is provided. */
  renderChip?: (args: {
    item: unknown | null;
    id: string;
    onRemove: () => void;
  }) => React.ReactNode;
  searchPlaceholder?: string;
  emptyLabel?: (term: string) => string;
  errorLabel?: string;
  loadingLabel?: string;
  groupHeading?: (term: string) => string | undefined;
  multiAddMorePlaceholder?: string;
  debounceMs?: number;
  cacheLimit?: number;
}
function TokenSelector(props) {
  const $ = _c(4);
  if (props.dataSource) {
    let t0;
    if ($[0] !== props) {
      t0 = <GenericPickerMode {...props} />;
      $[0] = props;
      $[1] = t0;
    } else {
      t0 = $[1];
    }
    return t0;
  }
  let t0;
  if ($[2] !== props) {
    t0 = <TokenSelectorRelayMode {...props} />;
    $[2] = props;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  return t0;
}
export default memo(TokenSelector);